import { Arguments } from '@repo-level/core-front';
import { CurrencyCode } from 'helpers/currency';
import { LocalStorage } from 'helpers/localStorage';
import { mailMask } from 'helpers/masks';
import { Locale } from 'languages';

import {
  always,
  identity,
  ifElse,
  isEmpty,
  isNil,
  over,
  pipe,
  lensProp,
  lensPath,
  prop,
  path,
  toString,
  is,
  replace,
  dissocPath,
  cond,
  complement,
  T,
} from 'ramda';

export type MethodFormApi2 = Pick<
  Arguments.SubmitArgs,
  'amountValue' | 'playerFieldsData'
> &
  Partial<Pick<Arguments.SubmitArgs, 'methodFieldsData'>>;

export type MethodFormApi3 = Pick<
  Arguments.SubmitArgs,
  'amountValue' | 'playerFieldsData'
> &
  Pick<Arguments.SubmitArgs, 'methodFieldsData'>;

const checkData = ifElse(isEmpty, always({}), identity);

const checkMethodFieldsData = ifElse(
  pipe(prop('methodFieldsData'), isNil),
  over(lensProp('methodFieldsData'), always({})),
  identity
);

const formatCardExpiry = (value: string) => {
  const [date, year] = value.split('/');
  return `20${year}-${date}`;
};

const formatCardExpiryDate = ifElse(
  pipe(path(['methodFieldsData', 'expiry_date']), isNil),
  identity,
  over(lensPath(['methodFieldsData', 'expiry_date']), formatCardExpiry)
);

const formatCardNumber = (data: MethodFormApi3): MethodFormApi3 => {
  const numberPath = methodFieldsPath.creditCardNumberField;
  const encCreditcardNumberPath = methodFieldsPath.creditCardNumberPath;

  const isFieldFilled = (p: string[]) =>
    complement(isNil)(path<string>(p, data));

  const numberIsFilled = isFieldFilled(numberPath);
  const encCreditcardNumberIsFilled = isFieldFilled(encCreditcardNumberPath);

  return cond([
    [() => numberIsFilled, over(lensPath(numberPath), mailMask)],
    [
      () => encCreditcardNumberIsFilled,
      over(lensPath(encCreditcardNumberPath), mailMask),
    ],
    [T, identity],
  ])(data);
};

const formatCardSeamlessExpiryDate = ifElse(
  pipe(path(['methodFieldsData', 'seamless_expiry_date']), isNil),
  identity,
  over(lensPath(['methodFieldsData', 'seamless_expiry_date']), formatCardExpiry)
);

const toNumber = (value: string | number) => +value;

const toStringMaybeString = ifElse(is(Number), toString, identity);

const formatAmountF = pipe<string | number, string, string, number>(
  toStringMaybeString,
  replace(/,/g, ''),
  toNumber
);

const formatAmount = ifElse(
  pipe(path(['amountValue']), isNil),
  identity,
  over(lensProp('amountValue'), formatAmountF)
);

export const formattingData = pipe<
  MethodFormApi2,
  MethodFormApi3,
  MethodFormApi3,
  MethodFormApi3,
  MethodFormApi3,
  MethodFormApi3,
  MethodFormApi3
>(
  checkData,
  checkMethodFieldsData,
  formatAmount,
  formatCardExpiryDate,
  formatCardSeamlessExpiryDate,
  formatCardNumber
);

export const paymentsStorage = new LocalStorage<
  { [id: string]: MethodFormApi2 },
  null
>('payments', null);

export const methodFieldsPath = {
  cvvPath: ['methodFieldsData', 'encCvv'],
  cvvField: ['methodFieldsData', 'cvv'],
  creditCardNumberPath: ['methodFieldsData', 'encCreditcardNumber'],
  creditCardNumberField: ['methodFieldsData', 'number'],
  amountValue: ['amountValue'],
};

type SensitiveData = {
  fieldPath: string[];
  replacer?: (arg: any) => void;
  toRemove?: boolean;
}[];

export const hideSensitiveData = (sensitiveData: SensitiveData) => (data: {
  [key: string]: any;
}) => {
  return sensitiveData.reduce(
    (updatedData, { fieldPath, toRemove, replacer }) => {
      if (path(fieldPath, updatedData)) {
        if (toRemove) {
          updatedData = dissocPath(fieldPath, updatedData);
        }

        if (replacer) {
          const fieldLens = lensPath(fieldPath);
          updatedData = over(fieldLens, replacer, updatedData);
        }
      }
      return updatedData;
    },
    { ...data }
  );
};

export enum AccountTitleType {
  CREDIT_CARD,
  EMAIL,
}

export const formatAccountTitle = (accountTitle: string) =>
  /^\d{16}$/.test(accountTitle)
    ? { title: accountTitle.slice(-4), type: AccountTitleType.CREDIT_CARD }
    : { title: accountTitle, type: AccountTitleType.EMAIL };

enum TemplateShortAmount {
  TEMPLATE_1,
  TEMPLATE_2,
  TEMPLATE_3,
  TEMPLATE_4,
  TEMPLATE_5,
  TEMPLATE_6,
  TEMPLATE_NO_FTD,
  TEMPLATE_NO_FTD_HIGHER,
}

const templateShortAmounts: { [template: number]: number[] } = {
  [TemplateShortAmount.TEMPLATE_1]: [20, 50, 100, 200, 500],
  [TemplateShortAmount.TEMPLATE_2]: [40, 100, 200, 500, 1000],
  [TemplateShortAmount.TEMPLATE_3]: [200, 500, 1000, 2000, 5000],
  [TemplateShortAmount.TEMPLATE_4]: [100, 200, 500, 1000, 2000],
  [TemplateShortAmount.TEMPLATE_5]: [50, 100, 200, 500, 1000],
  [TemplateShortAmount.TEMPLATE_6]: [30, 50, 100, 200, 500],
  [TemplateShortAmount.TEMPLATE_NO_FTD]: [25, 50, 100, 200, 500],
  [TemplateShortAmount.TEMPLATE_NO_FTD_HIGHER]: [30, 50, 100, 200, 500],
};

export const defaultProgressionForShortAmounts = [
  2,
  5,
  10,
  20,
  50,
  100,
  200,
  500,
  1000,
  2000,
  5000,
  10000,
  20000,
  50000,
  100000,
];

export const getDefaultAmounts = (min: number, max: number) => {
  const startIndex = defaultProgressionForShortAmounts.findIndex(
    (amount) => amount > min
  );

  return defaultProgressionForShortAmounts
    .slice(startIndex, startIndex + 5)
    .filter((amount) => amount <= max);
};

export const mapPresets: { [key: string]: number[] } = {
  [`${Locale.ENGLISH}_${CurrencyCode.EUR}`]: templateShortAmounts[
    TemplateShortAmount.TEMPLATE_1
  ],
};

export const HIGHER_PRESET = 'higher_';

export const mapPresetsNoFTD: { [key: string]: number[] } = {
  [`${CurrencyCode.EUR}`]: templateShortAmounts[
    TemplateShortAmount.TEMPLATE_NO_FTD
  ],
  [`${CurrencyCode.AUD}`]: templateShortAmounts[
    TemplateShortAmount.TEMPLATE_NO_FTD
  ],
  [`${HIGHER_PRESET}${CurrencyCode.EUR}`]: templateShortAmounts[
    TemplateShortAmount.TEMPLATE_NO_FTD_HIGHER
  ],
  [`${HIGHER_PRESET}${CurrencyCode.AUD}`]: templateShortAmounts[
    TemplateShortAmount.TEMPLATE_NO_FTD_HIGHER
  ],
};

export const SECURE_3D_IDENTIFIER = 'requesting_3d_secure';
export const CRYPTO_IDENTIFIER = 'wallet_regeneration_warning';
export const CRYPTO_METHOD = 'crypto';

type Errors = Record<string, any>;

const getFirstErrorFieldPath = (errors: Errors, prefix = ''): string | null => {
  for (const key in errors) {
    const value = errors[key];
    const path = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') return path;
    if (typeof value === 'object') {
      const nested = getFirstErrorFieldPath(value, path);
      if (nested) return nested;
    }
  }
  return null;
};

export const scrollToFirstError = (errors: Errors): void => {
  const firstField = getFirstErrorFieldPath(errors);
  if (!firstField) return;

  const input = document.querySelector(`[name="${firstField}"]`);
  if (input) {
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (input as HTMLElement).focus();
  }
};
